import * as Dialog from "@radix-ui/react-dialog";
import { useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Button, Field, IconButton } from "@/components/kit";
import { api } from "@/lib/api";

export default function NewProjectDialog({ open, onClose }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [address, setAddress] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const ok = address.trim().length >= 3;

  useEffect(() => {
    if (open) {
      setAddress("");
      setError(null);
      setBusy(false);
    }
  }, [open]);

  const create = async (e) => {
    e.preventDefault();
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      const p = await api("/projects", { method: "POST", body: { address: address.trim() } });
      qc.setQueryData(["projects"], (old) => [p, ...(old ?? [])]);
      qc.invalidateQueries({ queryKey: ["projects"] });
      navigate(`/projects/${p.id}`, { replace: true });
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-[hsl(226_70%_4%/0.7)] backdrop-blur-sm data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <Dialog.Content data-testid="new-property-screen" aria-describedby="new-property-desc"
          className="fixed inset-x-0 bottom-0 z-50 rounded-t-3xl border border-border bg-card p-6 pb-8 shadow-2xl outline-none data-[state=open]:animate-in data-[state=open]:slide-in-from-bottom-10 sm:inset-auto sm:left-1/2 sm:top-1/2 sm:w-full sm:max-w-lg sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-3xl sm:p-8">
          <div className="flex items-start gap-3">
            <Dialog.Title className="flex-1 text-2xl font-medium" data-testid="header-title">New property</Dialog.Title>
            <Dialog.Close asChild><IconButton icon={X} label="Close" className="-mr-2 -mt-2" data-testid="new-property-close-button" /></Dialog.Close>
          </div>
          <p id="new-property-desc" className="mt-2 text-base text-card-foreground">Start with the address. You can add price, rooms and highlights next.</p>
          <form onSubmit={create} className="mt-6 flex flex-col gap-6">
            <Field id="new-property-address" label="Property address" value={address} onChange={(e) => setAddress(e.target.value)}
              autoFocus placeholder="123 Ocean View Drive, San Diego" error={error} data-testid="new-property-address-input" />
            <Button type="submit" title="Create property" loading={busy} disabled={!ok} data-testid="new-property-create-button" />
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
